import React from 'react';

import { faSpinnerThird, faPlus, faTrashAlt, faFileLines, faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import {
    createSubscriptionPlanFormSchema,
    type CreateSubscriptionPlanFormSchemaValues,
    useCreateSubscriptionPlan,
} from 'src/features/subscriptions/api/useCreatePrepaidPlan';
import { useDeleteSubscriptionPlan } from 'src/features/subscriptions/api/useDeletePrepaidPlan';
import { useUpdateSubscriptionPlan } from 'src/features/subscriptions/api/useUpdatePrepaidPlan';
import SubscriptionPlanForm from 'src/features/subscriptions/components/forms/SubscriptionPlanForm';
import subscriptionMessages from 'src/features/subscriptions/utils/messages';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps, tableMessages } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

import type { PrepaidPlan } from 'src/features/subscriptions/types';

interface SubscriptionPlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    isDialogOpen?: boolean;
    selectedSubscriptionPlan?: PrepaidPlan;
    children?: React.ReactNode;
    isDelete?: boolean;
    removeTrigger?: boolean;
    onCloseDialog?: () => void;
}

export default function SubscriptionPlanFormModal<TData>({
    isDialogOpen = false,
    selectedSubscriptionPlan,
    className,
    semantics,
    children,
    isDelete,
    removeTrigger,
    onSubmit: updateTable,
    onCloseDialog,
}: SubscriptionPlanFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingRole } = useParking();
    const { parkingId } = useParams();

    const { mutate: createSubscriptionPlan, isPending: isPendingCreate } = useCreateSubscriptionPlan({
        onSuccess: ({ plan_name }: PrepaidPlan) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(subscriptionMessages.createSubscriptionPlanSuccess, {
                    plan_name,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: updateSubscriptionPlan, isPending: isPendingUpdate } = useUpdateSubscriptionPlan({
        onSuccess: ({ plan_name }) => {
            if (updateTable) updateTable();
            onOpenChange(false);
            toast({
                title: formatMessage(subscriptionMessages.updateSubscriptionPlanSuccess, {
                    plan_name,
                }),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const { mutate: deleteSubscriptionPlan, isPending: isPendingDelete } = useDeleteSubscriptionPlan({
        onSuccess: ({ plan_name }) => {
            if (updateTable) updateTable();
            toast({
                title: formatMessage(subscriptionMessages.deleteSubscriptionPlanSuccess, { plan_name }),
            });
        },
        onError: () => {
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });
    const {
        plan_name = '',
        languages = [],
        description = '',
        duration = {
            unit: undefined,
            value: 0,
        },
        allowedWeekDays = [],
        allowedHours = {
            fromHour: '00:00:00',
            toHour: '23:59:59',
        },
        dueAmount = 0,
        dueTaxPercent = 0.2,
        dueTaxAmount = 0,
        dueAmountWithTax = 0,
        endAtMidnight = undefined,
    } = selectedSubscriptionPlan || {};

    const form = useForm<CreateSubscriptionPlanFormSchemaValues>({
        resolver: zodResolver(createSubscriptionPlanFormSchema),
        defaultValues: {
            plan_name,
            languages,
            description,
            duration,
            allowedWeekDays,
            allowedHours,
            dueAmount,
            dueTaxPercent,
            dueTaxAmount,
            dueAmountWithTax,
            endAtMidnight,
        },
    });

    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(selectedSubscriptionPlan);
    }, [selectedSubscriptionPlan]);

    const onSubmit = (values: CreateSubscriptionPlanFormSchemaValues) => {
        if (!parkingId) return;
        const submitData = {
            parkingId: parkingId,
            ...values,
        };

        if (!selectedSubscriptionPlan?.id) return createSubscriptionPlan(submitData);
        if (isDelete) return deleteSubscriptionPlan({ planId: selectedSubscriptionPlan.id, parkingId: parkingId });
        return updateSubscriptionPlan({ planId: selectedSubscriptionPlan.id, ...submitData });
    };

    const onOpenChange = (isOpen: boolean) => {
        if (setIsModalOpen) setIsModalOpen(isOpen);
        if (!isOpen) {
            form.reset();
            if (onCloseDialog) onCloseDialog();
        }
    };

    const getDialogTitle = () => {
        if (isDelete) return formatMessage(subscriptionMessages.deleteSubscriptionPlan);

        if (selectedSubscriptionPlan?.id) return formatMessage(subscriptionMessages.editSubscriptionPlan);

        return formatMessage(tableMessages.new, {
            entry: semantics?.entryName?.toLowerCase(),
        });
    };

    const getDialogDescription = () => {
        if (isDelete) return formatMessage(subscriptionMessages.deleteSubscriptionPlanDescription);

        if (selectedSubscriptionPlan?.id) return formatMessage(subscriptionMessages.editSubscriptionPlanDescription);

        return formatMessage(subscriptionMessages.createSubscriptionPlanDescription);
    };

    const getDialogIcon = () => {
        if (isDelete) return faTrashAlt;
        if (selectedSubscriptionPlan?.id) return faEdit;
        return faFileLines;
    };

    if (parkingRole == MEMBERSHIP_ROLES.CLIENT) return <></>;
    return (
        <Dialog modal open={isModalOpen || isDialogOpen} onOpenChange={onOpenChange}>
            {!children && !selectedSubscriptionPlan && !removeTrigger && (
                <DialogTrigger asChild>
                    <Button variant="outline" className={cn('ml-auto', className)}>
                        <FontAwesomeIcon className="text-primary-700" icon={faPlus} />
                        {formatMessage(tableMessages.new, {
                            entry: semantics?.entryName?.toLowerCase(),
                        })}
                    </Button>
                </DialogTrigger>
            )}
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent onClick={(e) => e.stopPropagation()}>
                <DialogHeader className={cn(isDelete && 'flex-row gap-4')}>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={getDialogIcon()} />
                        <div>
                            <DialogTitle>{getDialogTitle()}</DialogTitle>
                            <DialogDescription>{getDialogDescription()}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />
                <DialogBody>
                    {isDelete ? (
                        <p className="text-gray-500">
                            {formatMessage(subscriptionMessages.confirmDeleteSubscriptionPlan, {
                                plan: selectedSubscriptionPlan?.plan_name,
                            })}
                        </p>
                    ) : (
                        <SubscriptionPlanForm form={form} onSubmit={onSubmit} />
                    )}
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        variant={isDelete ? 'destructiveLight' : 'default'}
                        disabled={isPendingCreate || isPendingUpdate || isPendingDelete}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPendingCreate || isPendingUpdate || isPendingDelete ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(
                            isDelete
                                ? subscriptionMessages.deleteSubscriptionPlan
                                : selectedSubscriptionPlan?.id
                                  ? subscriptionMessages.editSubscriptionPlan
                                  : subscriptionMessages.addSubscriptionPlan,
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
